import { useEffect, useMemo, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { useAuth, getDashboardBusiness } from '@cravo/auth'
import { vendorDashboardApi } from '@cravo/api-service'
import { useTranslation } from '@cravo/i18n'
import { PENDING_REQUESTS_EVENT } from '@/lib/dashboard-events'
import { appLogoUrl, tenantName as _tenantName } from '@/lib/tenant'
import { buildNavGroups } from '@/lib/nav-config'
import { useAiEnabled } from '@/lib/use-ai-enabled'

const APP_LOGO = appLogoUrl()
const APP_NAME = _tenantName()

interface SidebarProps {
  open?: boolean
  /**
   * When true, render the icon-only collapsed rail variant. Labels, section
   * headers, and brand subtitle are hidden; the rail still expands on hover
   * to show full labels (Notion / Vercel pattern). Mobile drawer takes
   * precedence — `mini` is ignored when `open` (drawer) is showing on a
   * narrow viewport.
   */
  mini?: boolean
  onNavigate?: () => void
}

export function Sidebar({ open, mini = false, onNavigate }: SidebarProps = {}) {
  const { t } = useTranslation()
  const { vendor } = useAuth()
  const businessName = getDashboardBusiness(vendor)?.businessName || t('sidebar.noBusiness')
  const [pendingCount, setPendingCount] = useState(0)

  const { enabled: aiEnabled } = useAiEnabled()
  const navGroups = useMemo(() => buildNavGroups(t, { aiEnabled }), [t, aiEnabled])

  useEffect(() => {
    let cancelled = false

    async function hydratePendingCount() {
      try {
        const response = await vendorDashboardApi.get()
        if (!cancelled) {
          setPendingCount(response.pendingRequests?.length ?? 0)
        }
      } catch {
        // Ignore sidebar badge bootstrap failures
      }
    }

    void hydratePendingCount()

    function handlePendingCount(event: Event) {
      const detail = (event as CustomEvent<{ count?: number }>).detail
      if (typeof detail?.count === 'number') {
        setPendingCount(detail.count)
      }
    }

    window.addEventListener(PENDING_REQUESTS_EVENT, handlePendingCount as EventListener)

    return () => {
      cancelled = true
      window.removeEventListener(PENDING_REQUESTS_EVENT, handlePendingCount as EventListener)
    }
  }, [])

  return (
    <aside
      className={`sidebar ${open ? 'open' : ''} ${mini ? 'mini' : ''}`}
      aria-label={t('sidebar.partnerSubtitle', { businessName })}
    >
      <div className="brand">
        <img src={APP_LOGO} alt={APP_NAME} style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 6, flexShrink: 0 }} />
        <div className="brand-text">
          <div className="brand-name">{APP_NAME}</div>
          <div className="brand-sub">{t('sidebar.partnerSubtitle', { businessName })}</div>
        </div>
      </div>

      {navGroups.map((group) => (
        <div key={group.key}>
          <div className="nav-section">{group.label}</div>
          {group.items.map((item) => (
            <NavLink
              key={item.key}
              to={item.path}
              end={item.path === '/'}
              onClick={() => onNavigate?.()}
              className={({ isActive }) =>
                `nav-item ${isActive ? 'active' : ''}`
              }
              // Tooltip via the native attribute — only shows when the
              // label text is hidden (i.e. in the collapsed `mini` rail).
              // Cheap, keyboard-accessible, and dismisses on its own.
              title={mini ? item.label : undefined}
              aria-label={mini ? item.label : undefined}
            >
              <item.icon size={18} />
              <span className="nav-label">{item.label}</span>
              {item.key === 'requests' && pendingCount > 0 && (
                <span className="nav-badge">{pendingCount}</span>
              )}
            </NavLink>
          ))}
        </div>
      ))}
    </aside>
  )
}
